import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import {
  type Capacidades,
  type CaminhoDeEntrada,
  type DestinoDeOpcao,
  type DestinosDisponiveis,
  type FaqDetalhe,
  type NoDoMenu,
  type TrafegoEEntradas,
  api,
  explicarMotivo,
} from '../lib/api.js';
import { EditorFaq } from './EditorFaq.js';

/**
 * Diálogos de ESTRUTURA do editor da árvore.
 *
 * Quatro operações que mudam a forma: criar opção (e, por ela, criar nó),
 * trocar o destino de uma opção, mover um caminho para outro pai e desativar
 * nó. Todas escrevem no rascunho; nenhuma chega ao aluno antes de publicar.
 *
 * O que estes diálogos existem para dizer, e que o servidor sozinho não diz na
 * hora certa:
 *
 *  - que o `no_id` NÃO se edita depois de criado, porque o histórico de
 *    atendimentos aponta para nós por ele;
 *  - quanta gente passou pelo nó no último mês, ANTES de desativá-lo;
 *  - que a FAQ criada aqui já responde pelo menu mas só entra na busca livre
 *    depois da reindexação;
 *  - que mover é mover UM caminho, e qual deles, quando há mais de um.
 */

const ROTULO_TIPO: Record<DestinoDeOpcao['tipo'], string> = {
  no: 'Abrir outro nó',
  no_novo: 'Criar nó novo',
  faq: 'Responder uma FAQ',
  duvidas: 'Pergunta livre por categoria',
  transbordo: 'Passar para a secretaria',
};

/** FAQ em branco para o formulário de criação — ver `EditorFaq`. */
const FAQ_EM_BRANCO: FaqDetalhe = {
  id: '',
  categoria: 'escola',
  pergunta: '',
  resposta: '',
  variacoes: [],
  ativo: true,
  reindexacaoPendente: false,
  atualizadoEm: new Date().toISOString(),
  trechosIndexados: 0,
  trechosSemEmbedding: [],
};

/**
 * Escolha de destino.
 *
 * Mesma peça na criação e na troca: duas listas de destino divergiriam no
 * primeiro tipo novo. Devolve `null` enquanto a escolha está incompleta — é o
 * que mantém o botão de confirmar desabilitado sem o diálogo precisar saber as
 * regras de cada tipo.
 */
export function EscolhaDeDestino({
  destinos,
  capacidades,
  aoMudar,
  semNoNovo = false,
}: {
  destinos: DestinosDisponiveis | null;
  capacidades: Capacidades;
  aoMudar: (destino: DestinoDeOpcao | null) => void;
  /** Esconde "criar nó novo" — na TROCA de destino ele não se aplica. */
  semNoNovo?: boolean;
}): JSX.Element {
  const [tipo, setTipo] = useState<DestinoDeOpcao['tipo']>('no');
  const [proximoNo, setProximoNo] = useState<string | null>(null);
  const [novoNoId, setNovoNoId] = useState('');
  const [novoTexto, setNovoTexto] = useState('');
  const [faqId, setFaqId] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [faqs, setFaqs] = useState<DestinosDisponiveis['faqs']>(destinos?.faqs ?? []);
  const [criandoFaq, setCriandoFaq] = useState(false);
  const [faqCriada, setFaqCriada] = useState<string | null>(null);

  // A busca de FAQ vai ao servidor: a base cresce e a lista não cabe na tela.
  useEffect(() => {
    const timer = setTimeout(() => {
      void api
        .get<DestinosDisponiveis>(`/admin/menu/destinos?busca=${encodeURIComponent(busca)}`)
        .then((r) => setFaqs(r.faqs))
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [busca]);

  /** Recalcula o destino a cada mudança e avisa quem hospeda o diálogo. */
  useEffect(() => {
    if (tipo === 'transbordo') return aoMudar({ tipo: 'transbordo' });
    if (tipo === 'no') return aoMudar(proximoNo ? { tipo: 'no', proximoNo } : null);
    if (tipo === 'faq') return aoMudar(faqId ? { tipo: 'faq', faqId } : null);
    if (tipo === 'duvidas') return aoMudar(categoria ? { tipo: 'duvidas', categoria } : null);
    return aoMudar(
      novoNoId.trim() && novoTexto.trim()
        ? { tipo: 'no_novo', noId: novoNoId.trim(), texto: novoTexto }
        : null,
    );
  }, [tipo, proximoNo, faqId, categoria, novoNoId, novoTexto, aoMudar]);

  const categoriaEscolhida = (destinos?.categorias ?? []).find((c) => c.categoria === categoria);

  const tipos = (Object.keys(ROTULO_TIPO) as DestinoDeOpcao['tipo'][]).filter(
    (t) => !(semNoNovo && t === 'no_novo'),
  );

  return (
    <Stack gap="sm">
      <Select
        label="O que esta opção faz"
        allowDeselect={false}
        value={tipo}
        onChange={(v) => setTipo((v ?? 'no') as DestinoDeOpcao['tipo'])}
        data={tipos.map((t) => ({ value: t, label: ROTULO_TIPO[t] }))}
      />

      {tipo === 'no' && (
        <Select
          label="Nó de destino"
          searchable
          placeholder="escolha o nó"
          value={proximoNo}
          onChange={setProximoNo}
          data={destinos?.nos ?? []}
        />
      )}

      {tipo === 'no_novo' && (
        <Stack gap="xs">
          <TextInput
            label="Identificador do nó"
            description="minúsculas, sem espaço — ex.: hangar, voo_noturno"
            value={novoNoId}
            onChange={(e) => setNovoNoId(e.currentTarget.value.toLowerCase().replace(/\s+/g, '_'))}
            error={
              novoNoId && !/^[a-z][a-z0-9_]{1,59}$/.test(novoNoId)
                ? 'comece com letra; use só minúsculas, números e _'
                : null
            }
          />
          <Alert color="orange" variant="light" title="O identificador não muda depois">
            O histórico de atendimentos aponta para os nós por este identificador. Renomear depois
            quebraria a leitura de conversas antigas, então ele é definitivo — o texto do nó, esse
            sim, você edita quando quiser.
          </Alert>
          <Textarea
            label="Texto do nó"
            description="é o que o aluno lê ao chegar aqui"
            autosize
            minRows={3}
            value={novoTexto}
            onChange={(e) => setNovoTexto(e.currentTarget.value)}
          />
        </Stack>
      )}

      {tipo === 'faq' && (
        <Stack gap="xs">
          <TextInput
            label="Buscar FAQ"
            placeholder="pergunta ou trecho da resposta"
            value={busca}
            onChange={(e) => setBusca(e.currentTarget.value)}
          />
          <Text size="xs" c="dimmed">
            Só FAQs ativas aparecem aqui: apontar um item de menu para FAQ
            inativa quebraria a publicação.
          </Text>
          <ScrollArea.Autosize mah={220} type="auto">
            <Stack gap={4}>
              {faqs.length === 0 && (
                <Text size="sm" c="dimmed">
                  Nenhuma FAQ ativa para esta busca.
                </Text>
              )}
              {faqs.map((f) => (
                <Paper
                  key={f.id}
                  withBorder={faqId === f.id}
                  bg={faqId === f.id ? 'var(--mantine-primary-color-light)' : undefined}
                  p="xs"
                  radius="sm"
                  onClick={() => setFaqId(f.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <Group gap={6} wrap="wrap">
                    <Text size="sm">{f.pergunta}</Text>
                    <Badge size="xs" variant="light">
                      {f.categoria}
                    </Badge>
                    {/* Os dois estados que mudam o que o aluno recebe. A lista
                        só traz FAQ ATIVA, então "inativa" não aparece aqui. */}
                    {f.reindexacaoPendente && (
                      <Badge size="xs" color="violet" variant="light">
                        aguarda reindexação
                      </Badge>
                    )}
                    {f.encaminhaParaSecretaria && (
                      <Badge size="xs" color="orange" variant="light">
                        encaminha para a secretaria
                      </Badge>
                    )}
                  </Group>
                </Paper>
              ))}
            </Stack>
          </ScrollArea.Autosize>

          {capacidades.editar && (
            <Button size="xs" variant="light" onClick={() => setCriandoFaq(true)}>
              Criar FAQ nova
            </Button>
          )}

          {faqCriada && (
            <Alert color="violet" variant="light" title="FAQ criada e já ligada a esta opção">
              O item de menu funciona de imediato — ele responde pelo id da FAQ. A{' '}
              <strong>busca livre</strong> só a encontra depois que um admin reindexar.
            </Alert>
          )}

          {/* O MESMO formulário da aba FAQs — e ele já é um modal por conta
              própria, com guarda de alteração não salva. Envolvê-lo em outro
              empilharia dois diálogos para a mesma tarefa. */}
          {criandoFaq && (
            <EditorFaq
              faq={FAQ_EM_BRANCO}
              capacidades={capacidades}
              categorias={(destinos?.categorias ?? []).map((c) => c.categoria)}
              categoriaPendenteConteudo="pendente_conteudo"
              aoFechar={() => setCriandoFaq(false)}
              aoSalvar={(criada) => {
                setCriandoFaq(false);
                setFaqId(criada.id);
                setFaqCriada(criada.id);
                setFaqs((atuais) => [
                  {
                    id: criada.id,
                    pergunta: criada.pergunta,
                    categoria: criada.categoria,
                    reindexacaoPendente: true,
                    encaminhaParaSecretaria: criada.categoria === 'pendente_conteudo',
                  },
                  ...atuais,
                ]);
              }}
            />
          )}
        </Stack>
      )}

      {tipo === 'duvidas' && (
        <Stack gap="xs">
          <Select
            label="Categoria que filtra a busca"
            searchable
            placeholder="escolha a categoria"
            value={categoria}
            onChange={setCategoria}
            /* O número de FAQs ativas vai no rótulo: é o que diz se a escolha
               serve. Zero significa que toda pergunta ali vira lacuna. */
            data={(destinos?.categorias ?? []).map((c) => ({
              value: c.categoria,
              label: `${c.categoria} — ${c.faqsAtivas} FAQ(s) ativa(s)`,
            }))}
          />
          {categoriaEscolhida?.faqsAtivas === 0 && (
            <Alert color="orange" variant="light" title="Nenhuma FAQ ativa nesta categoria">
              A busca filtrada não encontraria nada, e <strong>toda</strong> pergunta feita aqui
              viraria lacuna. Escreva uma FAQ nesta categoria antes, ou escolha outra.
            </Alert>
          )}
          <Text size="xs" c="dimmed">
            Só categorias que existem na base. Uma categoria inventada filtraria a busca para zero
            e o aluno receberia “não entendi” para uma pergunta que a base responde.
          </Text>
        </Stack>
      )}

      {tipo === 'transbordo' && (
        <Text size="sm" c="dimmed">
          A conversa passa para a secretaria, com protocolo, dentro e fora do horário.
        </Text>
      )}
    </Stack>
  );
}

/** Carrega os destinos uma vez por abertura de diálogo. */
export function useDestinos(aberto: boolean): DestinosDisponiveis | null {
  const [destinos, setDestinos] = useState<DestinosDisponiveis | null>(null);
  useEffect(() => {
    if (!aberto) return;
    void api
      .get<DestinosDisponiveis>('/admin/menu/destinos')
      .then(setDestinos)
      .catch(() => undefined);
  }, [aberto]);
  return destinos;
}

// ---------------------------------------------------------------------------

export function ModalNovaOpcao({
  no,
  capacidades,
  aoFechar,
  aoCriado,
}: {
  no: NoDoMenu;
  capacidades: Capacidades;
  aoFechar: () => void;
  aoCriado: () => void;
}): JSX.Element {
  const destinos = useDestinos(true);
  const [label, setLabel] = useState('');
  const [destino, setDestino] = useState<DestinoDeOpcao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const noTeto = no.vagasRestantes === 0;

  async function criar(): Promise<void> {
    if (!destino) return;
    setSalvando(true);
    try {
      await api.post('/admin/menu/opcoes', { noId: no.noId, label, destino });
      aoCriado();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível criar a opção'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal opened onClose={aoFechar} title={`Nova opção em ${no.noId}`} size="lg">
      <Stack gap="md">
        {noTeto ? (
          <Alert color="orange" variant="light" title="Este nó está no teto de 8 opções">
            O <strong>9</strong> é reservado para “falar com a secretaria” e o <strong>0</strong>{' '}
            para voltar, então sobram oito números. Para acrescentar uma opção aqui, remova outra
            ou mova parte do conteúdo para um nó novo.
          </Alert>
        ) : (
          <Text size="xs" c="dimmed">
            Cabem {no.vagasRestantes} opção(ões) neste nó. O número que o aluno digita vem da
            ordem, e o 9 não entra na conta.
          </Text>
        )}

        <TextInput
          label="Rótulo"
          description="o que o aluno lê ao lado do número"
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
          disabled={noTeto}
        />

        {!noTeto && (
          <EscolhaDeDestino
            destinos={destinos}
            capacidades={capacidades}
            aoMudar={setDestino}
          />
        )}

        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button onClick={aoFechar}>Cancelar</Button>
          <Button
            variant="filled"
            loading={salvando}
            disabled={noTeto || !label.trim() || !destino}
            onClick={() => void criar()}
          >
            Criar opção
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function ModalTrocarDestino({
  opcaoId,
  label,
  destinoAtual,
  capacidades,
  aoFechar,
  aoTrocado,
}: {
  opcaoId: string;
  label: string;
  destinoAtual: string;
  capacidades: Capacidades;
  aoFechar: () => void;
  aoTrocado: (semEntrada: string[]) => void;
}): JSX.Element {
  const destinos = useDestinos(true);
  const [destino, setDestino] = useState<DestinoDeOpcao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function trocar(): Promise<void> {
    if (!destino) return;
    setSalvando(true);
    try {
      const r = await api.patch<{ semEntrada: string[] }>(
        `/admin/menu/opcoes/${opcaoId}/destino`,
        { destino },
      );
      aoTrocado(r.semEntrada);
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível trocar o destino'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal opened onClose={aoFechar} title={`Destino de “${label}”`} size="lg">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Hoje: {destinoAtual}
        </Text>
        {/* Criar nó não cabe aqui: trocar destino é edição da ligação. */}
        <EscolhaDeDestino
          destinos={destinos}
          capacidades={capacidades}
          aoMudar={setDestino}
          semNoNovo
        />
        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button onClick={aoFechar}>Cancelar</Button>
          <Button
            variant="filled"
            loading={salvando}
            disabled={!destino}
            onClick={() => void trocar()}
          >
            Trocar destino
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Mover um caminho de um pai para outro.
 *
 * O título diz "mover um caminho", não "mover o nó", porque é isso que
 * acontece: um nó alcançado de dois lugares continua alcançável pelo outro. Com
 * mais de uma entrada, o diálogo OBRIGA a escolher qual — e diz, em texto, que
 * as outras ficam onde estão.
 */
export function ModalMoverNo({
  no,
  nos,
  aoFechar,
  aoMovido,
}: {
  no: NoDoMenu;
  nos: readonly NoDoMenu[];
  aoFechar: () => void;
  aoMovido: () => void;
}): JSX.Element {
  const [dados, setDados] = useState<TrafegoEEntradas | null>(null);
  const [opcaoId, setOpcaoId] = useState<string | null>(null);
  const [novoPai, setNovoPai] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<TrafegoEEntradas>(`/admin/menu/nos/${no.noId}/trafego`);
      setDados(r);
      // Uma entrada só: já escolhida, sem pergunta desnecessária.
      if (r.entradas.length === 1) setOpcaoId(r.entradas[0]!.opcaoId);
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível ler as entradas deste nó'));
    }
  }, [no.noId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const entradas: CaminhoDeEntrada[] = dados?.entradas ?? [];
  const varios = entradas.length > 1;

  async function mover(): Promise<void> {
    if (!opcaoId || !novoPai) return;
    setSalvando(true);
    try {
      await api.patch(`/admin/menu/opcoes/${opcaoId}/pai`, { paiNoId: novoPai });
      aoMovido();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível mover'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal opened onClose={aoFechar} title={`Mover um caminho até ${no.noId}`} size="lg">
      <Stack gap="md">
        {!dados && (
          <Group gap="xs">
            <Loader size="sm" />
            <Text c="dimmed">Carregando…</Text>
          </Group>
        )}

        {entradas.length === 0 && dados && (
          <Alert color="yellow" variant="light" title="Nenhum caminho chega a este nó">
            Não há opção apontando para <Code>{no.noId}</Code>, então não há o que mover. Crie uma
            opção no nó de onde ele deve pender.
          </Alert>
        )}

        {varios && (
          <Alert color="orange" variant="light" title="Este nó é alcançado por mais de um caminho">
            Mover troca o pai de <strong>um</strong> caminho; os outros continuam como estão. Se a
            intenção é que o nó penda de outro lugar <em>também</em>, o caminho é criar uma opção
            nova apontando para ele — não mover.
          </Alert>
        )}

        {entradas.length > 0 && (
          <Select
            label={varios ? 'Qual caminho mover' : 'Caminho'}
            allowDeselect={false}
            value={opcaoId}
            onChange={setOpcaoId}
            data={entradas.map((e) => ({
              value: e.opcaoId,
              label: `${e.paiNoId} → “${e.label}”`,
            }))}
          />
        )}

        <Select
          label="Novo pai"
          searchable
          placeholder="escolha o nó que vai conter este caminho"
          value={novoPai}
          onChange={setNovoPai}
          data={nos
            .filter((n) => n.ativo && n.noId !== no.noId)
            .map((n) => ({
              value: n.noId,
              label: `${n.noId}${n.vagasRestantes === 0 ? ' (no teto de 8)' : ''}`,
              disabled: n.vagasRestantes === 0,
            }))}
        />

        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button onClick={aoFechar}>Cancelar</Button>
          <Button
            variant="filled"
            loading={salvando}
            disabled={!opcaoId || !novoPai}
            onClick={() => void mover()}
          >
            Mover
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Desativar nó, com o tráfego do último mês na frente.
 *
 * Desativar um nó por onde passaram 40 pessoas é decisão diferente de desativar
 * um que ninguém usou, e o número precisa estar na tela ANTES do clique —
 * depois ele não serve para nada.
 */
export function ModalDesativarNo({
  no,
  aoFechar,
  aoDesativado,
}: {
  no: NoDoMenu;
  aoFechar: () => void;
  aoDesativado: () => void;
}): JSX.Element {
  const [dados, setDados] = useState<TrafegoEEntradas | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    void api
      .get<TrafegoEEntradas>(`/admin/menu/nos/${no.noId}/trafego`)
      .then(setDados)
      .catch(() => undefined);
  }, [no.noId]);

  const entradas = dados?.entradas ?? [];

  async function desativar(): Promise<void> {
    setSalvando(true);
    try {
      await api.post(`/admin/menu/nos/${no.noId}/desativar`, {});
      aoDesativado();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível desativar'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal opened onClose={aoFechar} title={`Desativar ${no.noId}?`} size="lg">
      <Stack gap="md">
        {!dados ? (
          <Group gap="xs">
            <Loader size="sm" />
            <Text c="dimmed">Consultando o tráfego…</Text>
          </Group>
        ) : (
          <Alert
            color={dados.trafego.atendimentos > 0 ? 'orange' : 'gray'}
            variant="light"
            title={
              dados.trafego.atendimentos > 0
                ? `${dados.trafego.atendimentos} atendimento(s) passaram por aqui nos últimos ${dados.trafego.dias} dias`
                : `Ninguém passou por aqui nos últimos ${dados.trafego.dias} dias`
            }
          >
            {dados.trafego.atendimentos > 0
              ? 'Desativar tira este caminho do menu na próxima publicação. Quem estava usando vai deixar de encontrá-lo.'
              : 'Desativar tira este caminho do menu na próxima publicação.'}
          </Alert>
        )}

        {entradas.length > 0 && (
          <Alert color="red" variant="light" title="Há opções apontando para este nó">
            <Stack gap={4}>
              <Text size="sm">
                Remova ou redirecione estas opções antes — uma opção que leva a um nó desativado
                aparece no menu e não leva a nada:
              </Text>
              {entradas.map((e) => (
                <Text size="sm" key={e.opcaoId}>
                  • <Code>{e.paiNoId}</Code> → “{e.label}”
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        <Text size="xs" c="dimmed">
          O nó <strong>não é apagado</strong>: o histórico de atendimentos aponta para ele por{' '}
          <Code>{no.noId}</Code> e precisa continuar resolvendo. O que muda é a presença na árvore
          publicada.
        </Text>

        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button onClick={aoFechar}>Cancelar</Button>
          <Button
            color="red"
            variant="filled"
            loading={salvando}
            disabled={entradas.length > 0}
            onClick={() => void desativar()}
          >
            Desativar mesmo assim
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Quem vê uma opção.
 *
 * O painel já MOSTRAVA a regra ("só aparece: aluno") e não tinha como
 * defini-la — a coluna só era escrita pelo seed. Sem isto, a etapa do menu por
 * tipo seria inerte: as regras existiriam no engine e ninguém conseguiria
 * marcá-las.
 */
export function ModalVisibilidade({
  opcaoId,
  label,
  regraAtual,
  menuPorTipoAtivo,
  aoFechar,
  aoDefinido,
}: {
  opcaoId: string;
  label: string;
  regraAtual: string | null;
  menuPorTipoAtivo: boolean;
  aoFechar: () => void;
  aoDefinido: () => void;
}): JSX.Element {
  const [regra, setRegra] = useState(regraAtual ?? '');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const OPCOES = [
    { value: '', label: 'Todo mundo (sem regra)' },
    { value: 'em_horario', label: 'Só dentro do horário de atendimento' },
    { value: 'fora_do_horario', label: 'Só fora do horário de atendimento' },
    { value: 'aluno', label: 'Só aluno identificado' },
    { value: 'instrutor', label: 'Só instrutor identificado' },
    { value: 'aluno_ou_instrutor', label: 'Só aluno ou instrutor identificado' },
    { value: 'interessado', label: 'Só quem não está no SAGA' },
  ];

  const ehDeTipo = ['aluno', 'instrutor', 'aluno_ou_instrutor', 'interessado'].includes(regra);

  async function salvar(): Promise<void> {
    setSalvando(true);
    try {
      await api.patch(`/admin/menu/opcoes/${opcaoId}/visibilidade`, {
        regra: regra === '' ? null : regra,
      });
      aoDefinido();
    } catch (e) {
      setErro(explicarMotivo(e, 'não foi possível definir quem vê'));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal opened onClose={aoFechar} title={`Quem vê “${label}”?`} size="lg">
      <Stack gap="md">
        <Select
          label="Aparece para"
          allowDeselect={false}
          value={regra}
          onChange={(v) => setRegra(v ?? '')}
          data={OPCOES}
        />

        {ehDeTipo && !menuPorTipoAtivo && (
          <Alert color="orange" variant="light" title="O menu por tipo está desligado">
            A regra fica gravada, mas <strong>não esconde nada hoje</strong>: a opção continua
            aparecendo para todo mundo. É de propósito — hoje o bot reconhece pouco mais da
            metade dos alunos pelo telefone, e esconder opção de quem ele não reconhece custaria
            o atendimento. Monte a árvore agora; ligar é mudar{' '}
            <Code>MENU_POR_TIPO_ATIVO</Code> no servidor.
          </Alert>
        )}

        {ehDeTipo && menuPorTipoAtivo && (
          <Alert color="orange" variant="light" title="Esta regra esconde a opção de verdade">
            Quem não se encaixa <strong>não vê</strong> este item, e a numeração do menu muda
            para essa pessoa. Confira se quem precisa da opção consegue ser identificado.
          </Alert>
        )}

        {erro && (
          <Alert color="red" variant="light" role="alert">
            {erro}
          </Alert>
        )}

        <Group justify="flex-end">
          <Button onClick={aoFechar}>Cancelar</Button>
          <Button variant="filled" loading={salvando} onClick={() => void salvar()}>
            Salvar
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
